<template>
  <div class="classification-dropdowns">
    <!-- Department Dropdown -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-gray-600">Department</label>
      <Dropdown 
        v-model="selectedDepartment" 
        :options="departments" 
        option-label="name" 
        option-value="id"
        data-key="id"
        :filter="false"
        placeholder="Select Department" 
        class="w-48" 
        :disabled="isLoadingDepartments"
        :loading="isLoadingDepartments"
        show-clear
        @change="onDepartmentChange"
      />
    </div>
        
    <!-- Master Category Dropdown -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-gray-600">Master Category</label>
      <Dropdown 
        :key="'mc-' + (selectedDepartment ?? 'x')"
        v-model="selectedMasterCategory" 
        :options="validMasterCategories" 
        option-label="name" 
        option-value="id"
        data-key="id"
        :filter="false"
        placeholder="Select Master Category" 
        class="w-48" 
        :disabled="!selectedDepartment || isLoadingMasterCategories"
        :loading="isLoadingMasterCategories"
        show-clear
        @change="onMasterCategoryChange"
      />
    </div>
        
    <!-- Category Dropdown -->
    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-gray-600">Category</label>
      <Dropdown 
        :key="'cat-' + (selectedMasterCategory ?? 'x')"
        v-model="selectedCategory" 
        :options="validCategories" 
        option-label="name" 
        option-value="id"
        data-key="id"
        :filter="false"
        placeholder="Select Category" 
        class="w-48" 
        :disabled="!selectedMasterCategory || isLoadingCategories"
        :loading="isLoadingCategories"
        show-clear
        @change="onCategoryChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { defineProps, defineEmits, defineExpose } from 'vue';
import { getFilterOptions, getWikiCatalogFilterOptions } from '@/services/projectService';

// Props
const props = defineProps({
    // Initial values
    initialDepartment: {
        type: [String, Number],
        default: null
    },
    initialMasterCategory: {
        type: [String, Number],
        default: null
    },
    initialCategory: {
        type: [String, Number],
        default: null
    },
    // Whether to load data on mount
    autoLoad: {
        type: Boolean,
        default: true
    },
    /** When true, use wiki merged taxonomy (catalog + company + global); otherwise company classifications only. */
    wikiCatalogOptions: {
        type: Boolean,
        default: false
    }
});

// Emits
const emit = defineEmits(['change', 'department-change', 'master-category-change', 'category-change']);

// Reactive state
const departments = ref([]);
const masterCategories = ref([]);
const categories = ref([]);
/** PrimeVue Select matches optionValue with strict equality; API/query ids are often strings. */
function normalizePositiveInt(v) {
    if (v === null || v === undefined || v === '') {
        return null;
    }
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
}

const selectedDepartment = ref(normalizePositiveInt(props.initialDepartment));
const selectedMasterCategory = ref(normalizePositiveInt(props.initialMasterCategory));
const selectedCategory = ref(normalizePositiveInt(props.initialCategory));

// Loading states
const isLoadingDepartments = ref(false);
const isLoadingMasterCategories = ref(false);
const isLoadingCategories = ref(false);
const isMounted = ref(false);

// Cache for filter options
const filterOptionsCache = ref(null);
const lastCacheRefresh = ref(null);
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/** Structure API sets department_id; flat endpoints may only have parent_id (department). */
function masterCategoryDepartmentId(mc) {
    const v = mc.department_id ?? mc.parent_id;
    return normalizePositiveInt(v);
}

/** Flat categories may use parent_id for master category. */
function categoryMasterCategoryId(cat) {
    const v = cat.master_category_id ?? cat.parent_id;
    return normalizePositiveInt(v);
}

// Computed properties for filtered options
const validMasterCategories = computed(() => {
    const d = normalizePositiveInt(selectedDepartment.value);
    if (!d) return [];
    return masterCategories.value.filter((mc) => masterCategoryDepartmentId(mc) === d);
});

const validCategories = computed(() => {
    const m = normalizePositiveInt(selectedMasterCategory.value);
    if (!m) return [];
    return categories.value.filter((cat) => categoryMasterCategoryId(cat) === m);
});

/** After options load, drop child selections that no longer belong under the parent. */
function pruneInvalidSelections() {
    const d = normalizePositiveInt(selectedDepartment.value);
    const m = normalizePositiveInt(selectedMasterCategory.value);
    const c = normalizePositiveInt(selectedCategory.value);
    if (!d && (m || c)) {
        selectedMasterCategory.value = null;
        selectedCategory.value = null;
        return;
    }
    // Do not clear selections when option lists are still empty (avoid wiping valid state before lists load).
    if (d && m && masterCategories.value.length > 0) {
        const mcOk = masterCategories.value.some(
            (mc) => masterCategoryDepartmentId(mc) === d && normalizePositiveInt(mc.id) === m
        );
        if (!mcOk) {
            selectedMasterCategory.value = null;
            selectedCategory.value = null;
            return;
        }
    }
    if (m && c && categories.value.length > 0) {
        const catOk = categories.value.some(
            (cat) => categoryMasterCategoryId(cat) === m && normalizePositiveInt(cat.id) === c
        );
        if (!catOk) {
            selectedCategory.value = null;
        }
    } else if (!m && c) {
        selectedCategory.value = null;
    }
}

// Load filter options from API
const loadFilterOptions = async () => {
    try {
        // Check cache first
        const now = Date.now();
        if (filterOptionsCache.value && lastCacheRefresh.value && (now - lastCacheRefresh.value) < CACHE_DURATION) {
            console.log('[ClassificationDropdowns] Using cached filter options');
            const cached = filterOptionsCache.value;
            departments.value = cached.departments;
            masterCategories.value = cached.masterCategories;
            categories.value = cached.categories;
            selectedDepartment.value = normalizePositiveInt(selectedDepartment.value);
            selectedMasterCategory.value = normalizePositiveInt(selectedMasterCategory.value);
            selectedCategory.value = normalizePositiveInt(selectedCategory.value);
            pruneInvalidSelections();
            return;
        }

        console.log('[ClassificationDropdowns] Loading filter options from API');
        isLoadingDepartments.value = true;
        
        const loader = props.wikiCatalogOptions ? getWikiCatalogFilterOptions : getFilterOptions;
        const filterOptions = await loader();
        
        departments.value = filterOptions.departments || [];
        masterCategories.value = filterOptions.masterCategories || [];
        categories.value = filterOptions.categories || [];

        selectedDepartment.value = normalizePositiveInt(selectedDepartment.value);
        selectedMasterCategory.value = normalizePositiveInt(selectedMasterCategory.value);
        selectedCategory.value = normalizePositiveInt(selectedCategory.value);
        pruneInvalidSelections();
        
        // Only cache non-empty trees so a transient empty response (or stale scope) is not stuck for CACHE_DURATION.
        const hasDepartments = Array.isArray(departments.value) && departments.value.length > 0;
        if (hasDepartments) {
            filterOptionsCache.value = filterOptions;
            lastCacheRefresh.value = now;
        }
        
    } catch (error) {
        console.error('[ClassificationDropdowns] Error loading filter options:', error);
    } finally {
        isLoadingDepartments.value = false;
    }
};

// Event handlers
const onDepartmentChange = (event) => {
    const raw = event?.value !== undefined ? event.value : event;
    const departmentId = normalizePositiveInt(raw);
    selectedDepartment.value = departmentId;
    selectedMasterCategory.value = null;
    selectedCategory.value = null;

    if (isMounted.value) {
        emit('department-change', departmentId);
        emit('change', {
            department: departmentId,
            masterCategory: null,
            category: null
        });
    }
};

const onMasterCategoryChange = (event) => {
    const raw = event?.value !== undefined ? event.value : event;
    const masterCategoryId = normalizePositiveInt(raw);
    selectedMasterCategory.value = masterCategoryId;
    selectedCategory.value = null;

    if (isMounted.value) {
        emit('master-category-change', masterCategoryId);
        emit('change', {
            department: selectedDepartment.value,
            masterCategory: masterCategoryId,
            category: null
        });
    }
};

const onCategoryChange = (event) => {
    const raw = event?.value !== undefined ? event.value : event;
    const categoryId = normalizePositiveInt(raw);
    selectedCategory.value = categoryId;

    if (isMounted.value) {
        emit('category-change', categoryId);
        emit('change', {
            department: selectedDepartment.value,
            masterCategory: selectedMasterCategory.value,
            category: categoryId
        });
    }
};

// Keep dropdowns in sync when the parent applies filters from the URL (Wiki / Equipment).
// Without this, initial props are read only once at setup — often before ?department= is applied.
watch(
  () => props.initialDepartment,
  (newVal) => {
    const v = normalizePositiveInt(newVal);
    if (selectedDepartment.value !== v) {
      selectedDepartment.value = v;
    }
  }
);

watch(
  () => props.initialMasterCategory,
  (newVal) => {
    const v = normalizePositiveInt(newVal);
    if (selectedMasterCategory.value !== v) {
      selectedMasterCategory.value = v;
    }
  }
);

watch(
  () => props.initialCategory,
  (newVal) => {
    const v = normalizePositiveInt(newVal);
    if (selectedCategory.value !== v) {
      selectedCategory.value = v;
    }
  }
);

// Load data on mount if autoLoad is true
onMounted(() => {
    isMounted.value = true;
    if (props.autoLoad) {
        loadFilterOptions();
    }
});

// Expose methods for external use
defineExpose({
    loadFilterOptions,
    clearAll: () => {
        selectedDepartment.value = null;
        selectedMasterCategory.value = null;
        selectedCategory.value = null;
    },
    clearMasterCategory: () => {
        selectedMasterCategory.value = null;
        selectedCategory.value = null;
    },
    clearCategory: () => {
        selectedCategory.value = null;
    },
    getValues: () => ({
        department: selectedDepartment.value,
        masterCategory: selectedMasterCategory.value,
        category: selectedCategory.value
    })
});
</script>

<style scoped>
.classification-dropdowns {
    @apply flex flex-wrap gap-4 items-center;
}
</style>
